<template>
  <div class="container">
    <Toolbar class="mt-2 mb-2">
      <template #start>
        <h2>
          <span v-if="isUpdate">
            {{ $t('roamingteampage.update') }}
          </span>
          <span v-else>{{ $t('roamingteampage.create') }}</span>
        </h2>
      </template>
    </Toolbar>
    <form @submit.prevent="submit">
      <div class="p-input mb-3">
        <label
          for="name"
          class="required-input"
        >{{ $t('entity.roamingTeam.name') }}</label>
        <InputText
          id="name"
          v-model="v$.roamingTeam.name.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.roamingTeam.name.$error }"
          type="text"
          maxlength="30"
        />
        <div
          v-for="error of v$.roamingTeam.name.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label
          for="availabilityDate"
          class="required-input"
        >{{ $t('entity.roamingTeam.availabilityDates') }}</label>
        <Calendar
          id="availabilityDate"
          v-model="selectedDates"
          selection-mode="range"
          class="col-12 p-0 mt-2"
          :class="{'invalid-input': v$.roamingTeam.availabilityStartDate.$error || v$.roamingTeam.availabilityEndDate.$error }"
          type="text"
          @date-select="selectDates"
        />
        <div
          v-for="error of v$.roamingTeam.availabilityStartDate.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>

        <div
          v-for="error of v$.roamingTeam.availabilityEndDate.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <h2>{{ $t('entity.roamingTeam.planning') }}</h2>
      <div
        v-if="roamingTeam.planning"
        class="grid"
      >
        <RoamingTeamDayPlanningForm
          id="mondayplanning"
          v-model="v$.roamingTeam.planning.mondayPlanning.$model"
          :title="$t('entity.planning.monday')"
          class="col-12 md:col-6 lg:col-3"
        />
        <RoamingTeamDayPlanningForm
          id="tuesdayplanning"
          v-model="v$.roamingTeam.planning.tuesdayPlanning.$model"
          :title="$t('entity.planning.tuesday')"
          class="col-12 md:col-6 lg:col-3"
        />
        <RoamingTeamDayPlanningForm
          id="wednesdayplanning"
          v-model="v$.roamingTeam.planning.wednesdayPlanning.$model"
          :title="$t('entity.planning.wednesday')"
          class="col-12 md:col-6 lg:col-3"
        />
        <RoamingTeamDayPlanningForm
          id="thursdayplanning"
          v-model="v$.roamingTeam.planning.thursdayPlanning.$model"
          :title="$t('entity.planning.thursday')"
          class="col-12 md:col-6 lg:col-3"
        />
        <RoamingTeamDayPlanningForm
          id="fridayplanning"
          v-model="v$.roamingTeam.planning.fridayPlanning.$model"
          :title="$t('entity.planning.friday')"
          class="col-12 md:col-6 lg:col-3"
        />
        <RoamingTeamDayPlanningForm
          id="saturdayplanning"
          v-model="v$.roamingTeam.planning.saturdayPlanning.$model"
          :title="$t('entity.planning.saturday')"
          class="col-12 md:col-6 lg:col-3"
        />
        <RoamingTeamDayPlanningForm
          id="sundayplanning"
          v-model="v$.roamingTeam.planning.sundayPlanning.$model"
          :title="$t('entity.planning.sunday')"
          class="col-12 md:col-6 lg:col-3"
        />
      </div>

      <Message
        v-if="errors.length > 0"
        severity="error"
        :closable="false"
      >
        <ul class="list-none m-0 p-0">
          <li
            v-for="error of errors"
            :key="error.content"
          >
            {{ $t(error) }}
          </li>
        </ul>
      </Message>

      <div class="my-3 flex justify-content-between flex-wrap">
        <Button
          :label="$t('action.back')"
          icon="pi pi-arrow-left"
          class="p-button-secondary p-button-rounded p-button-outlined"
          @click="toRoamingTeamTable"
        />
        <Button
          type="submit"
          :label="isUpdate ? $t('action.crud.update') : $t('action.crud.create')"
          icon="pi pi-save"
          class="p-button-rounded p-button-outlined"
        />
      </div>
    </form>
  </div>
</template>

<script>
import {useVuelidate} from "@vuelidate/core";
import {required} from "@/i18n/i18nValidators";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import RoamingTeamDayPlanningForm from "@/components/roamingTeam/RoamingTeamDayPlanningForm.vue";
import moment from "moment";
import Calendar from "primevue/calendar";
import Message from "primevue/message";
import Toolbar from "primevue/toolbar";

export default {
  name: 'RoamingTeamForm',
  components: {RoamingTeamDayPlanningForm, Toolbar, InputText, Button, Calendar, Message},
  setup() {
    return {
      v$: useVuelidate()
    }
  },
  data() {
    return {
      roamingTeam: {
        name: '',
        availabilityStartDate: null,
        availabilityEndDate: null,
        planning: {
          mondayPlanning: null,
          tuesdayPlanning: null,
          wednesdayPlanning: null,
          thursdayPlanning: null,
          fridayPlanning: null,
          saturdayPlanning: null,
          sundayPlanning: null,
        },
      },
      selectedDates: [],
      errors: []
    }
  },
  computed: {
    isUpdate() {
      return this.$route.params.id;
    }
  },
  mounted() {
    if (this.isUpdate) this.fetchRoamingTeam(this.$route.params.id);
  },
  validations() {
    return {
      roamingTeam: {
        name: {required},
        availabilityStartDate: {required},
        availabilityEndDate: {required},
        planning: {
          mondayPlanning: {},
          tuesdayPlanning: {},
          wednesdayPlanning: {},
          thursdayPlanning: {},
          fridayPlanning: {},
          saturdayPlanning: {},
          sundayPlanning: {},
        }
      }
    }
  },
  methods: {
    async fetchRoamingTeam(id) {
      try {
        this.roamingTeam = await this.$roamingTeamService.fetchById(id);
        if (!this.roamingTeam) await this.$router.push("/roamingTeam");
        this.selectedDates = [
          new Date(this.roamingTeam.availabilityStartDate),
          new Date(this.roamingTeam.availabilityEndDate)
        ]
      } catch (error) {
        this.$emitter.emit('toast', {
          severity: 'error',
          message: error.response.data.map(error => this.$t(error)).join(', ')
        })
        await this.$router.push("/roamingTeam");
      }
    },
    selectDates() {
      this.v$.roamingTeam.availabilityStartDate.$model = null
      this.v$.roamingTeam.availabilityEndDate.$model = null
      if (this.selectedDates && this.selectedDates.length === 2) {
        if (this.selectedDates[0] !== null) {
          this.v$.roamingTeam.availabilityStartDate.$model = moment(this.selectedDates[0]).format('YYYY-MM-DD')
        }
        if (this.selectedDates[1] !== null) {
          this.v$.roamingTeam.availabilityEndDate.$model = moment(this.selectedDates[1]).format('YYYY-MM-DD')
        }
      }
    },
    async submit() {
      const formErrors = await this.v$.$validate()
      if (!formErrors) return

      this.errors = []
      await this.createOrUpdate()
    },
    toRoamingTeamTable() {
      this.$router.push({name: 'roamingTeam'})
    },

    async createOrUpdate() {
      try {
        if (this.isUpdate) {
          await this.$roamingTeamService.update(this.roamingTeam)
          this.$emitter.emit('toast', {severity: 'success', message: 'roamingteampage.toast.updateSuccess'})
        } else {
          await this.$roamingTeamService.create(this.roamingTeam)
          this.$emitter.emit('toast', {severity: 'success', message: 'roamingteampage.toast.createSuccess'})
        }
        this.toRoamingTeamTable()
      } catch (e) {
        this.errors = e.response.data;
      }
    }
  }
}
</script>
